import { cloneElement, isValidElement, type JSX } from 'react';

import { Badge, cn } from '../../../../v2';
import { TextEyebrow } from '../../../../v2/components/text-eyebrow';
import { ExpandableBody, ExpandableRow } from '../../../expandable-row';
import type { BaseCardProps } from '../../types';
import { IconTitleSubtitleBlock } from '../icon-title-subtitle-block';
import { SelectableControl } from '../selectable-control';

export function DefaultSizeDetailCard(props: BaseCardProps): JSX.Element {
  const {
    actions,
    assistiveText,
    badgeProps,
    eyebrowProps,
    icon,
    subtitle,
    subtitleComponent,
    title,
    children,
    hasActions,
    expanded,
    setExpanded,
    selectableOptions,
    isSelectable,
    isExpandable,
    isHighlighted,
    hasEyebrow,
    isClickable,
    showSubtitleComponentWhenExpanded,
    testId,
    disabledProps,
    cardProps = {},
    searchTerm,
  } = props;
  const hasBadge = !!badgeProps;
  const { isDisabled } = disabledProps ?? {};

  const innerPaddingClassName = `p-1`;

  const { className: eyebrowClassName } = eyebrowProps ?? {};

  return (
    <div className="flex">
      {isSelectable && (
        <div className={innerPaddingClassName}>
          <SelectableControl selectableOptions={selectableOptions} testId={testId} disabled={isDisabled} />
        </div>
      )}
      <div className="flex flex-1 flex-col justify-center">
        <ExpandableRow removeStyling expanded={expanded}>
          <div className="flex flex-col">
            <div className="flex items-center justify-between">
              <div className="flex flex-col justify-center">
                {hasEyebrow ? (
                  <div className={innerPaddingClassName}>
                    <TextEyebrow
                      className={cn('text-brand-400 dark:text-brand-300', eyebrowClassName)}
                      data-testid={`${testId}-eyebrow`}
                    >
                      {eyebrowProps?.text}
                    </TextEyebrow>
                  </div>
                ) : (
                  <div className={innerPaddingClassName}>
                    <IconTitleSubtitleBlock
                      expanded={expanded}
                      showSubtitleComponentWhenExpanded={showSubtitleComponentWhenExpanded}
                      icon={icon}
                      setExpanded={setExpanded}
                      showChevron={isExpandable}
                      subtitle={subtitle}
                      subtitleComponent={subtitleComponent}
                      title={title}
                      isHighlighted={isHighlighted}
                      testId={testId}
                      cardProps={cardProps}
                      searchTerm={searchTerm}
                    />
                  </div>
                )}
              </div>
              <div className={cn('flex items-center gap-[6px]', innerPaddingClassName)}>
                {hasBadge && (
                  <Badge variant={badgeProps?.type ? badgeProps.type : 'success'} data-testid={`${testId}-badge`}>
                    {badgeProps.content}
                  </Badge>
                )}
                {hasActions &&
                  actions?.map((action, i) => (
                    <div
                      // eslint-disable-next-line react/no-array-index-key
                      key={`action-for-${title}-${i}`}
                      {...(isClickable && {
                        onClick: (e) => e.stopPropagation(),
                        role: 'button',
                        tabIndex: 0,
                        onKeyDown: (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.stopPropagation();
                          }
                        },
                      })}
                    >
                      {isDisabled && isValidElement(action)
                        ? cloneElement<{ disabled?: boolean }>(action as JSX.Element, { disabled: isDisabled })
                        : action}
                    </div>
                  ))}
              </div>
            </div>
            {hasEyebrow && (
              <IconTitleSubtitleBlock
                expanded={expanded}
                showSubtitleComponentWhenExpanded={showSubtitleComponentWhenExpanded}
                icon={icon}
                setExpanded={setExpanded}
                showChevron={isExpandable}
                subtitle={subtitle}
                subtitleComponent={subtitleComponent}
                title={title}
                testId={testId}
                isHighlighted={isHighlighted}
                cardProps={cardProps}
                searchTerm={searchTerm}
              />
            )}
          </div>
          <ExpandableBody paddingX={0} paddingBottom={0}>
            <div className={innerPaddingClassName}>
              {children}
              {assistiveText && (
                <div className="pt-1">
                  <div className="text-sm font-light" data-testid={`${testId}-assistive-text`}>
                    {assistiveText}
                  </div>
                </div>
              )}
            </div>
          </ExpandableBody>
        </ExpandableRow>
      </div>
    </div>
  );
}
